import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export function useUnreadSupportCount() {
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const { count, error } = await supabase
        .from('support_messages')
        .select('id', { count: 'exact', head: true })
        .eq('sender', 'user')
        .eq('is_read_by_admin', false);

      if (!error) {
        setUnreadCount(count || 0);
      }
    } catch (error) {
      console.error('Error fetching unread support count:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();

    // Subscribe to realtime updates for new messages
    const channel = supabase
      .channel('admin-support-unread-count')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'support_messages',
      }, (payload) => {
        // Only increment if it's a user message (not admin)
        if (payload.new && payload.new.sender === 'user') {
          setUnreadCount(prev => prev + 1);
        }
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'support_messages',
      }, (payload) => {
        // If a message was marked as read, refetch the count
        if (payload.new && payload.old) {
          const wasUnread = !payload.old.is_read_by_admin;
          const isNowRead = payload.new.is_read_by_admin;
          if (wasUnread && isNowRead && payload.new.sender === 'user') {
            setUnreadCount(prev => Math.max(0, prev - 1));
          }
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchUnreadCount]);

  return { unreadCount, isLoading, refetch: fetchUnreadCount };
}
